const IMAGE_SENT_LABEL = '[Imagem enviada]';
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const VALID_ROLES = new Set(['user', 'assistant', 'human_agent', 'system']);
const POLL_INTERVAL_MS = 30000;

const HANDOFF_PROMPTS = {
  human_support: 'Descreva sua dúvida ou problema. Sua mensagem será encaminhada ao suporte humano — a resposta pode levar algumas horas.',
  improvement: 'Descreva sua sugestão de melhoria. Ela será enviada à nossa equipe — a resposta pode levar algumas horas.',
};

function normalizeImageFile(file) {
  if (!file || !ALLOWED_IMAGE_TYPES.includes(file.type)) return null;
  if (file.name) return file;
  const ext = file.type === 'image/jpeg' ? 'jpg' : file.type.split('/')[1] || 'png';
  return new File([file], `print-${Date.now()}.${ext}`, { type: file.type });
}

function extractImageFile(dataTransfer) {
  if (!dataTransfer) return null;

  const items = dataTransfer.items ? [...dataTransfer.items] : [];
  for (const item of items) {
    if (item.kind !== 'file' || !item.type.startsWith('image/')) continue;
    const file = item.getAsFile();
    const normalized = normalizeImageFile(file);
    if (normalized) return normalized;
  }

  const files = dataTransfer.files ? [...dataTransfer.files] : [];
  for (const file of files) {
    const normalized = normalizeImageFile(file);
    if (normalized) return normalized;
  }

  return null;
}

function normalizeMessage(item) {
  if (!item || !VALID_ROLES.has(item.role)) return null;
  const content = typeof item.content === 'string' ? item.content.trim() : '';
  if (!content && !item.hasImage) return null;
  return {
    id: item.id || null,
    role: item.role,
    kind: item.kind || 'chat',
    content: content || (item.hasImage ? IMAGE_SENT_LABEL : ''),
    createdAt: item.createdAt || item.created_at || new Date().toISOString(),
    hasImage: Boolean(item.hasImage || item.has_image),
    imageUrl: item.imageUrl || null,
    ticketRef: item.ticketRef || item.ticket_ref || '',
  };
}

function formatChatTime(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function dayKey(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function formatDateSeparator(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (dayKey(date.toISOString()) === dayKey(today.toISOString())) return 'Hoje';
  if (dayKey(date.toISOString()) === dayKey(yesterday.toISOString())) return 'Ontem';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

function renderMarkdown(text) {
  if (typeof marked !== 'undefined' && typeof DOMPurify !== 'undefined') {
    const html = marked.parse(text, { breaks: true });
    return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  }
  return text.replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('\n', '<br>');
}

function createDateSeparatorElement(label) {
  const wrapper = document.createElement('div');
  wrapper.className = 'assistant-date-separator';
  wrapper.innerHTML = `<span>${label}</span>`;
  return wrapper;
}

function createMessageElement(message, options = {}) {
  const { humanSupportActive = false, onReplySupport = null } = options;
  const wrapper = document.createElement('div');
  wrapper.className = `assistant-message assistant-message--${message.role}`;

  const bubble = document.createElement('div');
  bubble.className = 'assistant-bubble';

  const body = document.createElement('div');
  body.className = 'assistant-bubble-body';

  if (message.role === 'human_agent') {
    const label = document.createElement('div');
    label.className = 'assistant-bubble-label';
    label.textContent = 'Suporte humano';
    body.appendChild(label);
  }

  if (message.hasImage) {
    if (message.imageUrl) {
      const img = document.createElement('img');
      img.src = message.imageUrl;
      img.alt = 'Imagem enviada';
      img.className = 'assistant-bubble-image';
      body.appendChild(img);
    } else {
      const placeholder = document.createElement('p');
      placeholder.className = 'assistant-bubble-image-placeholder';
      placeholder.textContent = IMAGE_SENT_LABEL;
      body.appendChild(placeholder);
    }
  }

  if (['assistant', 'human_agent', 'system'].includes(message.role)) {
    const markdown = document.createElement('div');
    markdown.className = message.role === 'system' ? 'assistant-bubble-text' : 'assistant-markdown';
    if (message.role === 'system') {
      markdown.textContent = message.content;
    } else {
      markdown.innerHTML = renderMarkdown(message.content);
    }
    body.appendChild(markdown);
  } else if (message.content && message.content !== IMAGE_SENT_LABEL) {
    const text = document.createElement('p');
    text.className = 'assistant-bubble-text';
    text.textContent = message.content;
    body.appendChild(text);
  }

  if (
    message.role === 'human_agent'
    && !humanSupportActive
    && typeof onReplySupport === 'function'
  ) {
    const replyBtn = document.createElement('button');
    replyBtn.type = 'button';
    replyBtn.className = 'assistant-reply-support-btn';
    replyBtn.textContent = 'Responder ao suporte';
    replyBtn.dataset.replySupport = '1';
    if (message.ticketRef) {
      replyBtn.dataset.ticketRef = message.ticketRef;
    }
    replyBtn.addEventListener('click', () => onReplySupport(message.ticketRef || ''));
    body.appendChild(replyBtn);
  }

  bubble.appendChild(body);

  const timeLabel = formatChatTime(message.createdAt);
  if (timeLabel) {
    const timeEl = document.createElement('span');
    timeEl.className = 'assistant-bubble-time';
    timeEl.textContent = timeLabel;
    bubble.appendChild(timeEl);
  }

  wrapper.appendChild(bubble);
  return wrapper;
}

function createTypingElement() {
  const wrapper = document.createElement('div');
  wrapper.className = 'assistant-message assistant-message--assistant';
  wrapper.id = 'assistant-typing';
  wrapper.innerHTML = `
    <div class="assistant-bubble assistant-typing">
      <span class="assistant-thinking-text">Pensando</span><span class="assistant-thinking-dots">...</span>
    </div>
  `;
  return wrapper;
}

function historyForApi(messages) {
  return messages
    .filter((msg) => msg.role === 'user' || msg.role === 'assistant')
    .map((msg) => ({
      role: msg.role,
      content: msg.hasImage && !msg.content ? IMAGE_SENT_LABEL : msg.content,
    }));
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Falha ao ler a imagem.'));
        return;
      }
      const base64 = result.split(',')[1] || '';
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Falha ao ler a imagem.'));
    reader.readAsDataURL(file);
  });
}

function isMobileViewport() {
  return window.matchMedia('(max-width: 1023px)').matches;
}

function formatFetchError(response, data) {
  if (data?.error) return data.error;
  if (response.status === 403) return 'Acesso negado. Atualize a página e tente novamente.';
  if (response.status === 503) return 'Assistente temporariamente indisponível. Tente novamente em instantes.';
  if (response.status === 429) return 'Muitas requisições. Aguarde um momento e tente novamente.';
  return `Erro ao enviar mensagem (HTTP ${response.status}).`;
}

function initAssistant() {
  const root = document.getElementById('assistant-root');
  if (!root) return;

  const apiUrl = root.dataset.apiUrl;
  const supportUrl = root.dataset.supportUrl;
  const supportEndUrl = root.dataset.supportEndUrl;
  const supportActivateUrl = root.dataset.supportActivateUrl;
  const conversationUrl = root.dataset.conversationUrl;
  const conversationClearUrl = root.dataset.conversationClearUrl;
  const conversationPollUrl = root.dataset.conversationPollUrl;
  const conversationUnreadUrl = root.dataset.conversationUnreadUrl;
  const csrfToken = root.dataset.csrfToken;
  const imageMaxMb = Number.parseInt(root.dataset.imageMaxMb || '2', 10);
  const imageMaxBytes = imageMaxMb * 1024 * 1024;

  const toggleBtn = document.getElementById('assistant-toggle');
  const drawer = document.getElementById('assistant-drawer');
  const overlay = document.getElementById('assistant-overlay');
  const closeBtn = document.getElementById('assistant-close');
  const clearBtn = document.getElementById('assistant-clear');
  const messagesEl = document.getElementById('assistant-messages');
  const emptyEl = document.getElementById('assistant-empty');
  const form = document.getElementById('assistant-form');
  const input = document.getElementById('assistant-input');
  const sendBtn = document.getElementById('assistant-send');
  const attachBtn = document.getElementById('assistant-attach');
  const imageInput = document.getElementById('assistant-image-input');
  const footerEl = document.querySelector('.assistant-footer');
  const bodyEl = document.querySelector('.assistant-body');
  const dropOverlay = document.getElementById('assistant-drop-overlay');
  const errorEl = document.getElementById('assistant-error');
  const usageEl = document.getElementById('assistant-usage');
  const unreadBadge = document.getElementById('assistant-unread-badge');
  const humanBanner = document.getElementById('assistant-human-banner');
  const humanBannerDetail = document.getElementById('assistant-human-banner-detail');
  const humanEndBtn = document.getElementById('assistant-human-end');
  const quickActionsEl = document.getElementById('assistant-quick-actions');

  let messages = [];
  let panelOpen = false;
  let sending = false;
  let sessionTokens = 0;
  let pendingImage = null;
  let pendingImageUrl = null;
  let imagePreview = null;
  let imagePreviewImg = null;
  let dragDepth = 0;
  let handoffMode = null;
  let humanSupportActive = false;
  let activeTicketRef = '';
  let pollTimer = null;
  let conversationLoaded = false;

  function setError(text) {
    if (!text) {
      errorEl.classList.add('hidden');
      errorEl.textContent = '';
      return;
    }
    errorEl.textContent = text;
    errorEl.classList.remove('hidden');
  }

  function updateUnreadBadge(count) {
    if (!unreadBadge) return;
    if (count > 0) {
      unreadBadge.textContent = count > 9 ? '9+' : String(count);
      unreadBadge.classList.remove('hidden');
    } else {
      unreadBadge.classList.add('hidden');
    }
  }

  async function fetchUnreadCount() {
    if (!conversationUnreadUrl) return;
    try {
      const response = await fetch(conversationUnreadUrl, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (response.status === 401 || response.status === 403) {
        stopPolling();
        updateUnreadBadge(0);
        return;
      }
      if (!response.ok) return;
      const data = await response.json();
      if (!panelOpen) {
        updateUnreadBadge(data.unread_count || 0);
      }
    } catch {
      /* ignore */
    }
  }

  function removeImagePreview() {
    if (imagePreview) {
      imagePreview.remove();
      imagePreview = null;
      imagePreviewImg = null;
    }
  }

  function showImagePreview(url) {
    if (!footerEl || !form) return;
    removeImagePreview();

    imagePreview = document.createElement('div');
    imagePreview.id = 'assistant-image-preview';
    imagePreview.className = 'assistant-image-preview';

    imagePreviewImg = document.createElement('img');
    imagePreviewImg.className = 'assistant-image-preview-img';
    imagePreviewImg.alt = '';
    imagePreviewImg.src = url;

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'assistant-image-remove';
    removeBtn.setAttribute('aria-label', 'Remover imagem');
    removeBtn.textContent = '×';
    removeBtn.addEventListener('click', clearPendingImage);

    imagePreview.appendChild(imagePreviewImg);
    imagePreview.appendChild(removeBtn);
    footerEl.insertBefore(imagePreview, form);
  }

  function clearPendingImage() {
    if (pendingImageUrl) {
      URL.revokeObjectURL(pendingImageUrl);
    }
    pendingImage = null;
    pendingImageUrl = null;
    if (imageInput) imageInput.value = '';
    removeImagePreview();
  }

  function setPendingImage(file) {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('Use JPEG, PNG ou WebP (capturas de tela ou fotos pequenas).');
      return;
    }
    if (file.size > imageMaxBytes) {
      setError(`Imagem muito grande (máximo ${imageMaxMb} MB). Envie um print ou foto compacta.`);
      return;
    }
    clearPendingImage();
    pendingImage = file;
    pendingImageUrl = URL.createObjectURL(file);
    showImagePreview(pendingImageUrl);
    setError('');
    input?.focus();
  }

  function handleIncomingImage(file) {
    if (!panelOpen || sending || !file) return;
    setPendingImage(file);
  }

  function setDropOverlayActive(active) {
    if (!dropOverlay) return;
    dropOverlay.classList.toggle('assistant-drop-overlay--active', active);
    dropOverlay.setAttribute('aria-hidden', active ? 'false' : 'true');
  }

  function resetDragState() {
    dragDepth = 0;
    setDropOverlayActive(false);
  }

  function updateUsageDisplay() {
    if (sessionTokens > 0) {
      usageEl.textContent = `${sessionTokens.toLocaleString('pt-BR')} tokens nesta sessão`;
      usageEl.classList.remove('hidden');
    } else {
      usageEl.classList.add('hidden');
    }
  }

  function updateHandoffPlaceholder() {
    if (!input) return;
    if (humanSupportActive || handoffMode === 'human_support') {
      input.placeholder = 'Digite sua mensagem para o suporte humano…';
    } else if (handoffMode === 'improvement') {
      input.placeholder = 'Descreva sua sugestão de melhoria…';
    } else {
      input.placeholder = 'Digite uma mensagem…';
    }
  }

  function applyHumanSupportState(active, ticketRef = '') {
    humanSupportActive = Boolean(active);
    activeTicketRef = ticketRef || '';
    if (humanBanner) {
      humanBanner.classList.toggle('is-active', humanSupportActive);
      humanBanner.setAttribute('aria-hidden', humanSupportActive ? 'false' : 'true');
    }
    if (humanBannerDetail) {
      humanBannerDetail.textContent = activeTicketRef
        ? `Ticket ${activeTicketRef} — suas mensagens vão direto para nossa equipe.`
        : 'Suas mensagens vão direto para nossa equipe.';
    }
    quickActionsEl?.classList.toggle('hidden', humanSupportActive);
    updateHandoffPlaceholder();
  }

  function renderMessages() {
    messagesEl.querySelectorAll('.assistant-message, .assistant-date-separator').forEach((el) => el.remove());
    const typing = document.getElementById('assistant-typing');
    if (typing) typing.remove();

    if (messages.length === 0) {
      emptyEl.classList.remove('hidden');
    } else {
      emptyEl.classList.add('hidden');
      let lastDay = '';
      messages.forEach((msg) => {
        const currentDay = dayKey(msg.createdAt);
        if (currentDay && currentDay !== lastDay) {
          const separator = formatDateSeparator(msg.createdAt);
          if (separator) {
            messagesEl.insertBefore(createDateSeparatorElement(separator), emptyEl);
          }
          lastDay = currentDay;
        }
        messagesEl.insertBefore(
          createMessageElement(msg, {
            humanSupportActive,
            onReplySupport: handleReplyToSupport,
          }),
          emptyEl,
        );
      });
    }

    if (sending) {
      messagesEl.appendChild(createTypingElement());
    }

    messagesEl.scrollTop = messagesEl.scrollHeight;
    clearBtn.disabled = messages.length === 0;
  }

  function setBodyScrollLock(locked) {
    if (!isMobileViewport()) return;
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  function mergeIncomingMessages(incoming) {
    if (!Array.isArray(incoming) || incoming.length === 0) return;
    const existingKeys = new Set(
      messages.map((m) => m.id || `${m.role}:${m.createdAt}:${m.content.slice(0, 40)}`),
    );
    incoming.forEach((raw) => {
      const normalized = normalizeMessage(raw);
      if (!normalized) return;
      const key = normalized.id || `${normalized.role}:${normalized.createdAt}:${normalized.content.slice(0, 40)}`;
      if (existingKeys.has(key)) return;
      existingKeys.add(key);
      messages.push(normalized);
    });
    messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    renderMessages();
  }

  async function loadConversation(force = false) {
    if (!conversationUrl || (conversationLoaded && !force)) return;
    try {
      const response = await fetch(conversationUrl, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) return;
      const data = await response.json();
      messages = (data.messages || []).map(normalizeMessage).filter(Boolean);
      applyHumanSupportState(data.human_support_active, data.active_ticket_ref);
      conversationLoaded = true;
      renderMessages();
      updateUnreadBadge(0);
    } catch {
      /* ignore */
    }
  }

  function lastMessageTimestamp() {
    if (messages.length === 0) return '';
    return messages[messages.length - 1].createdAt;
  }

  async function pollConversation() {
    if (!conversationPollUrl || !panelOpen) return;
    const since = lastMessageTimestamp();
    const url = since ? `${conversationPollUrl}?since=${encodeURIComponent(since)}` : conversationPollUrl;
    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (response.status === 401 || response.status === 403) {
        stopPolling();
        return;
      }
      if (!response.ok) return;
      const data = await response.json();
      mergeIncomingMessages(data.messages);
      applyHumanSupportState(
        Boolean(data.human_support_active),
        data.active_ticket_ref || '',
      );
      updateUnreadBadge(data.unread_count || 0);
    } catch {
      /* ignore */
    }
  }

  function startPolling() {
    stopPolling();
    pollTimer = window.setInterval(pollConversation, POLL_INTERVAL_MS);
  }

  function stopPolling() {
    if (pollTimer) {
      window.clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function setPanelOpen(open) {
    panelOpen = open;
    drawer.classList.toggle('assistant-drawer--open', open);
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    overlay.classList.toggle('hidden', !open);
    overlay.setAttribute('aria-hidden', open ? 'false' : 'true');
    setBodyScrollLock(open);
    if (toggleBtn) {
      toggleBtn.classList.toggle('assistant-help-btn--active', open);
      toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    if (open) {
      loadConversation(true).then(() => {
        pollConversation();
        startPolling();
      });
      updateUnreadBadge(0);
      setTimeout(() => input?.focus(), 100);
    } else {
      resetDragState();
      stopPolling();
      conversationLoaded = false;
      handoffMode = null;
      fetchUnreadCount();
    }
  }

  function enterHandoffMode(kind) {
    if (kind === 'human_support' && humanSupportActive) {
      setPanelOpen(true);
      setError('');
      input?.focus();
      return;
    }
    handoffMode = kind;
    updateHandoffPlaceholder();
    setPanelOpen(true);
    setError('');
    const notice = {
      role: 'system',
      content: HANDOFF_PROMPTS[kind] || HANDOFF_PROMPTS.human_support,
      createdAt: new Date().toISOString(),
      kind: 'handoff_notice',
    };
    messages = [...messages, notice];
    renderMessages();
    input?.focus();
  }

  async function sendSupportMessage(text, kindOverride = null) {
    const trimmed = text.trim();
    const kind = kindOverride || handoffMode || (humanSupportActive ? 'human_support' : null);
    if (!trimmed || sending || !kind) return;

    setError('');
    const previousMessages = [...messages];
    const now = new Date().toISOString();
    const userMessage = {
      role: 'user',
      content: trimmed,
      createdAt: now,
      kind,
      ticketRef: activeTicketRef,
    };

    messages = [...messages, userMessage];
    sending = true;
    sendBtn.disabled = true;
    attachBtn.disabled = true;
    renderMessages();
    input.value = '';
    const wasHandoff = handoffMode;
    handoffMode = null;

    try {
      const response = await fetch(supportUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ kind, message: trimmed }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(formatFetchError(response, data));
      }

      applyHumanSupportState(data.human_support_active, data.active_ticket_ref);
      if (kind === 'human_support' && wasHandoff === 'human_support' && data.reply) {
        messages = [
          ...messages,
          {
            role: 'system',
            content: data.reply,
            createdAt: new Date().toISOString(),
            kind: 'handoff_notice',
            ticketRef: data.ticket_ref || activeTicketRef,
          },
        ];
      }
      conversationLoaded = true;
      updateHandoffPlaceholder();
    } catch (err) {
      messages = previousMessages;
      handoffMode = wasHandoff;
      updateHandoffPlaceholder();
      const message = err instanceof TypeError
        ? 'Falha de conexão. Verifique sua internet e tente novamente.'
        : (err.message || 'Erro ao encaminhar ao suporte.');
      setError(message);
    } finally {
      sending = false;
      sendBtn.disabled = false;
      attachBtn.disabled = false;
      renderMessages();
      if (panelOpen) input?.focus();
    }
  }

  async function endHumanSupport() {
    if (!supportEndUrl || sending) return;
    setError('');
    sending = true;
    sendBtn.disabled = true;
    attachBtn.disabled = true;

    try {
      const response = await fetch(supportEndUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify({}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(formatFetchError(response, data));
      }

      applyHumanSupportState(false, '');
      if (data.reply) {
        messages = [
          ...messages,
          {
            role: 'system',
            content: data.reply,
            createdAt: new Date().toISOString(),
            kind: 'handoff_notice',
          },
        ];
      }
    } catch (err) {
      const message = err instanceof TypeError
        ? 'Falha de conexão. Verifique sua internet e tente novamente.'
        : (err.message || 'Erro ao encerrar suporte humano.');
      setError(message);
    } finally {
      sending = false;
      sendBtn.disabled = false;
      attachBtn.disabled = false;
      renderMessages();
    }
  }

  async function handleReplyToSupport(ticketRef = '') {
    if (!supportActivateUrl || sending) return;
    setError('');
    setPanelOpen(true);

    try {
      const response = await fetch(supportActivateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ ticket_ref: ticketRef || undefined }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(formatFetchError(response, data));
      }
      applyHumanSupportState(data.human_support_active, data.active_ticket_ref);
      renderMessages();
      input?.focus();
    } catch (err) {
      const message = err instanceof TypeError
        ? 'Falha de conexão. Verifique sua internet e tente novamente.'
        : (err.message || 'Erro ao retomar suporte humano.');
      setError(message);
    }
  }

  async function sendMessage(text, imageFile = null) {
    if (humanSupportActive && !imageFile) {
      await sendSupportMessage(text, 'human_support');
      return;
    }

    if (handoffMode && !imageFile) {
      await sendSupportMessage(text);
      return;
    }

    if (humanSupportActive && imageFile) {
      setError('No modo suporte humano, envie apenas texto. Volte ao assistente IA para anexar imagens.');
      return;
    }

    const trimmed = text.trim();
    const file = imageFile || pendingImage;
    if ((!trimmed && !file) || sending) return;

    setError('');
    const previousMessages = [...messages];
    const now = new Date().toISOString();
    const imageUrl = file ? URL.createObjectURL(file) : null;

    const userMessage = {
      role: 'user',
      content: trimmed,
      createdAt: now,
      hasImage: Boolean(file),
      imageUrl,
    };

    const optimistic = [...messages, userMessage];
    messages = optimistic;
    sending = true;
    sendBtn.disabled = true;
    attachBtn.disabled = true;
    renderMessages();

    const savedImage = file;
    clearPendingImage();
    input.value = '';

    try {
      const payload = {
        message: trimmed,
        history: historyForApi(previousMessages),
      };

      if (savedImage) {
        const base64 = await fileToBase64(savedImage);
        payload.image = {
          data: base64,
          mime_type: savedImage.type,
        };
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(formatFetchError(response, data));
      }

      messages = [
        ...optimistic,
        {
          role: 'assistant',
          content: data.reply,
          createdAt: new Date().toISOString(),
        },
      ];
      if (data.usage?.total_tokens) {
        sessionTokens += data.usage.total_tokens;
        updateUsageDisplay();
      }
      conversationLoaded = true;
    } catch (err) {
      messages = previousMessages;
      if (imageUrl) URL.revokeObjectURL(imageUrl);
      const message = err instanceof TypeError
        ? 'Falha de conexão. Verifique sua internet e tente novamente.'
        : (err.message || 'Erro ao comunicar com o assistente.');
      setError(message);
    } finally {
      sending = false;
      sendBtn.disabled = false;
      attachBtn.disabled = false;
      renderMessages();
      if (panelOpen) input?.focus();
    }
  }

  async function clearConversation() {
    if (sending) return;

    setError('');
    sending = true;
    sendBtn.disabled = true;
    attachBtn.disabled = true;

    try {
      if (!conversationClearUrl) {
        throw new Error('Limpeza de conversa indisponível.');
      }

      const response = await fetch(conversationClearUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify({}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(formatFetchError(response, data));
      }

      messages = [];
      sessionTokens = 0;
      handoffMode = null;
      applyHumanSupportState(
        Boolean(data.human_support_active),
        data.active_ticket_ref || '',
      );
      clearPendingImage();
      updateUsageDisplay();
      conversationLoaded = false;
      renderMessages();
      updateUnreadBadge(0);
    } catch (err) {
      const message = err instanceof TypeError
        ? 'Falha de conexão. Verifique sua internet e tente novamente.'
        : (err.message || 'Erro ao limpar conversa.');
      setError(message);
    } finally {
      sending = false;
      sendBtn.disabled = false;
      attachBtn.disabled = false;
      renderMessages();
    }
  }

  function isTypingTarget() {
    const tag = document.activeElement?.tagName?.toLowerCase();
    return tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable;
  }

  toggleBtn?.addEventListener('click', () => setPanelOpen(!panelOpen));
  closeBtn?.addEventListener('click', () => setPanelOpen(false));
  overlay?.addEventListener('click', () => setPanelOpen(false));
  clearBtn?.addEventListener('click', clearConversation);
  humanEndBtn?.addEventListener('click', endHumanSupport);

  function handlePaste(event) {
    if (!panelOpen || sending) return;
    const file = extractImageFile(event.clipboardData);
    if (!file) return;
    event.preventDefault();
    handleIncomingImage(file);
  }

  attachBtn?.addEventListener('click', () => imageInput?.click());
  imageInput?.addEventListener('change', () => {
    const file = imageInput.files?.[0];
    if (file) setPendingImage(file);
  });

  input?.addEventListener('paste', handlePaste);
  drawer?.addEventListener('paste', handlePaste);
  messagesEl?.addEventListener('paste', handlePaste);

  bodyEl?.addEventListener('dragenter', (event) => {
    if (!panelOpen || sending) return;
    event.preventDefault();
    dragDepth += 1;
    setDropOverlayActive(true);
  });

  bodyEl?.addEventListener('dragover', (event) => {
    if (!panelOpen || sending) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  });

  bodyEl?.addEventListener('dragleave', (event) => {
    if (!panelOpen || sending) return;
    event.preventDefault();
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) setDropOverlayActive(false);
  });

  bodyEl?.addEventListener('drop', (event) => {
    if (!panelOpen || sending) return;
    event.preventDefault();
    resetDragState();
    const file = extractImageFile(event.dataTransfer);
    if (file) handleIncomingImage(file);
  });

  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    sendMessage(input.value, pendingImage);
  });

  document.querySelectorAll('.assistant-suggestion, .assistant-quick-action').forEach((btn) => {
    btn.addEventListener('click', () => {
      const handoff = btn.dataset.handoff;
      if (handoff) {
        enterHandoffMode(handoff);
        return;
      }
      const text = btn.dataset.suggestion || btn.textContent;
      sendMessage(text);
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && panelOpen) {
      event.preventDefault();
      setPanelOpen(false);
      return;
    }
    if (
      event.key.toLowerCase() === 'h' &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey &&
      !isTypingTarget()
    ) {
      event.preventDefault();
      setPanelOpen(!panelOpen);
    }
  });

  renderMessages();
  applyHumanSupportState(false, '');
  fetchUnreadCount();
  window.setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
}

document.addEventListener('DOMContentLoaded', initAssistant);

export default initAssistant;
